import {
  createAsyncThunk,
  createSelector,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";

import { fetchDineinLayout } from "@/lib/api";
import type { DineinFloor, RestaurantTable } from "@/types";
import type { RootState } from "..";

/**
 * The `dinein` slice — module **96459**.
 *
 * The original receives the whole floor plan as the `dineinLayout` key of its
 * one bundled data call and folds it in with `setInitiateDineIn`. That reducer
 * does four things, all reproduced verbatim below:
 *
 * 1. sorts each floor's tables by name, `localeCompare` with
 *    `{ sensitivity: "base" }` — so `t10` sorts beside `T1`, not before `T2`;
 * 2. sorts the floors themselves by `sort_id`;
 * 3. builds `mappedData` — floor id → floor;
 * 4. flattens every table into `allTables` and `mappedTables`, **stamping
 *    `floor_id` and `floor_name` onto each one**. Those two fields are not
 *    stored on a table; the slice is where they come from, which is why the
 *    picker and the checkout row can both name a table's floor.
 *
 * It is persisted, exactly as the original persists it (`ms-staff-ordering-storage`
 * whitelists `dinein`), because the layout is the one thing `/review-order`
 * needs and never fetches for itself.
 *
 * `setUpdateDineinTable` IS here, and it is NOT a socket reducer — the original
 * storefront has no socket at all (no `socket.io`, `WebSocket`, `EventSource` or
 * `onSnapshot` appears anywhere in its bundle). Its one caller is the resume
 * lookup, `order/fetchDineinOrderDetails`, which splits the response and
 * dispatches the TABLE half unconditionally, before it even looks at the order
 * (`live-app-formatted.js`, thunk `order/fetchDineinOrderDetails`):
 *
 * ```js
 * let { data: { order: e, ...t } } = await a.Q.get(
 *   "/dinein-tables/…/…?with_order=true");
 * if ((o((0, b.eV)(t)), null != e)) { … }        // b.eV === setUpdateDineinTable
 * ```
 *
 * That is one of the two ways a table's seated state stays current between full
 * layout loads (the other is the 40-minute `refetchKey` reload on
 * `/menu/dine-in`): every lookup writes the server's fresh `num_of_guests` /
 * `order_id` / `seated_at` back onto the floor plan. Omitting it meant the
 * picker went on rendering the copy of the table it was first handed, however
 * long ago that was — and, because this slice is persisted, across reloads too.
 *
 * What is still NOT here: `endDineinSitting` (a `PUT /dinein-tables/:id` that
 * frees a table). It is a staff action taken on a POS terminal, and nothing in
 * this tree calls it. Recorded rather than stubbed.
 */
export interface DineinState {
  data: DineinFloor[];
  mappedData: Record<string, DineinFloor>;
  allTables: RestaurantTable[];
  mappedTables: Record<string, RestaurantTable>;
  loading: boolean;
}

const initialState: DineinState = {
  data: [],
  mappedData: {},
  allTables: [],
  mappedTables: {},
  loading: false,
};

/**
 * Rebuild `mappedData` / `allTables` / `mappedTables` from an ALREADY ORDERED
 * `data`, stamping each table with its floor.
 *
 * Split out because the original has this half twice, byte for byte:
 * `setInitiateDineIn` sorts and then indexes, `setUpdateDineinTable` re-indexes
 * WITHOUT re-sorting (it is patching one table in place, and a re-sort there
 * would be a second, silent behaviour).
 */
const reindexLayout = (data: DineinFloor[]): Omit<DineinState, "loading"> => {
  const mappedData: Record<string, DineinFloor> = {};
  const mappedTables: Record<string, RestaurantTable> = {};
  const allTables: RestaurantTable[] = [];

  data.forEach((floor) => {
    mappedData[floor._id] = { ...floor, tables: floor.tables };
    floor.tables.forEach((table) => {
      const stamped = { ...table, floor_id: floor._id, floor_name: floor.name };
      allTables.push(stamped);
      mappedTables[table._id] = stamped;
    });
  });

  return { data, mappedData, allTables, mappedTables };
};

/**
 * The slice's own flattener, shared by the reducer and the rehydrate path.
 *
 * `{ numeric: true, sensitivity: "base" }` is the LIVE build's comparator —
 * `e.name.localeCompare(t.name, "en", { numeric: !0, sensitivity: "base" })`.
 * The earlier `app-formatted.js` build omits `numeric`, which is why this read
 * `{ sensitivity: "base" }` alone and sorted `T10` between `T1` and `T2`.
 */
const indexLayout = (floors: DineinFloor[]): Omit<DineinState, "loading"> =>
  reindexLayout(
    floors
      .map((floor) => ({
        ...floor,
        tables: [...(floor.tables ?? [])].sort((a, b) =>
          a.name.localeCompare(b.name, "en", {
            numeric: true,
            sensitivity: "base",
          }),
        ),
      }))
      .sort((a, b) => (a.sort_id ?? 0) - (b.sort_id ?? 0)),
  );

/**
 * `GET /web/tables/:tenantId` — this tree's per-resource stand-in for the
 * `dineinLayout` key. Only the dine-in menu asks for it, as in the original.
 */
export const loadDineinLayout = createAsyncThunk(
  "dinein/loadDineinLayout",
  (tenantId: string) => fetchDineinLayout(tenantId),
);

const dineinSlice = createSlice({
  name: "dinein",
  initialState,
  reducers: {
    setInitiateDineIn(state, action: PayloadAction<DineinFloor[]>) {
      return { ...state, ...indexLayout(action.payload ?? []), loading: false };
    },

    /**
     * `setUpdateDineinTable` — patch ONE table in place from a server read.
     *
     * Verbatim from module 96459:
     *
     * ```js
     * setUpdateDineinTable: (e, t) => {
     *   let r = e.data.map((e) => e._id === t.payload.dinein_floor_id
     *     ? { ...e, tables: e.tables.map((e) =>
     *         e._id === t.payload._id ? { ...e, ...t.payload } : e) }
     *     : e), …                       // then the same re-index as above
     * }
     * ```
     *
     * The floor is matched on the payload's `dinein_floor_id` — a field the
     * EMBEDDED `dinein_floors.tables[]` records do not carry, and which
     * `GET /web/dinein-tables/:tenant/:table` stamps on for exactly this reason.
     * A payload without it silently patches nothing, so bail rather than
     * re-index for no change.
     */
    setUpdateDineinTable(
      state,
      action: PayloadAction<Partial<RestaurantTable> & { _id?: string; dinein_floor_id?: string }>,
    ) {
      const patch = action.payload;
      if (!patch?._id || !patch.dinein_floor_id) return state;
      const data = state.data.map((floor) =>
        floor._id === patch.dinein_floor_id
          ? {
              ...floor,
              tables: floor.tables.map((table) =>
                table._id === patch._id ? { ...table, ...patch } : table,
              ),
            }
          : floor,
      );
      return { ...state, ...reindexLayout(data), loading: false };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadDineinLayout.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadDineinLayout.fulfilled, (state, action) => ({
        ...state,
        ...indexLayout(action.payload ?? []),
        loading: false,
      }))
      .addCase(loadDineinLayout.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { setInitiateDineIn, setUpdateDineinTable } = dineinSlice.actions;

/**
 * Module **54755** — `getTableFromMappedData`. One table, by its floor and its
 * own id, with the floor stamped on. `null` when either id is missing or the
 * pair does not resolve, which is what makes the checkout row fall back to
 * "Select Table".
 */
export const findTableInLayout = ({
  mappedData,
  floorId,
  tableId,
}: {
  mappedData: Record<string, DineinFloor>;
  floorId?: string | null;
  tableId?: string | null;
}): (RestaurantTable & { floor_id: string; floor_name: string }) | null => {
  if (!floorId || !tableId) return null;
  const floor = mappedData[floorId];
  const table = floor?.tables.find((entry) => entry._id === tableId);
  if (!floor || !table) return null;
  return { ...table, floor_id: floor._id, floor_name: floor.name };
};

/**
 * `am` — the selector the dine-in checkout row reads.
 *
 * It is a `createSelector` for the reason the original's is one: it returns a
 * NEW object (the table with its floor stamped on), so an unmemoized version
 * hands React a different reference on every render and `useSelector` warns
 * about it — which it duly did before this was memoized.
 */
export const selectDineinTable = createSelector(
  [
    (state: RootState) => state.dinein.mappedData,
    (_state: RootState, floorId?: string | null) => floorId,
    (_state: RootState, _floorId?: string | null, tableId?: string | null) =>
      tableId,
  ],
  (mappedData, floorId, tableId) =>
    findTableInLayout({ mappedData, floorId, tableId }),
);

/**
 * `Fp` (module 96459) — narrow the layout to a staff member's `assigned_tables`.
 *
 * A `null` list is NOT an empty one: the original returns the layout untouched,
 * so a member of staff with no assignment sees every floor. Only a real list
 * narrows, and it rebuilds the floors from the tables rather than filtering
 * them, so a floor with no assigned table disappears entirely.
 */
export const narrowLayoutToAssignedTables = (
  data: DineinFloor[],
  mappedData: Record<string, DineinFloor>,
  mappedTables: Record<string, RestaurantTable>,
  assignedTables?: string[] | null,
): { dineinData: DineinFloor[]; dineinMappedData: Record<string, DineinFloor> } => {
  if (assignedTables == null) return { dineinData: data, dineinMappedData: mappedData };
  const narrowed: Record<string, DineinFloor> = {};
  for (const tableId of assignedTables) {
    const table = mappedTables[tableId];
    if (table == null) continue;
    const floor = table.floor_id ? mappedData[table.floor_id] : undefined;
    if (floor == null) continue;
    narrowed[floor._id] = {
      ...floor,
      tables: [...(narrowed[floor._id]?.tables ?? []), table],
    };
  }
  return { dineinData: Object.values(narrowed), dineinMappedData: narrowed };
};

export default dineinSlice.reducer;
